import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminApi, AdminProduct, FitReference } from '../services/adminApi';
import { api } from '../services/api';
import { getMeasurementsForCategories, getProductTypesForCategories } from '../utils/categoryMeasurements';
import CategoryMultiSelect from '../components/CategoryMultiSelect';
import { API_BASE_URL } from '../config/environment';
import './AddProduct.css';

interface Category {
  id: number;
  name: string;
  description?: string;
}

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submittingStatus, setSubmittingStatus] = useState<'published' | 'draft' | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [fitReferences, setFitReferences] = useState<FitReference[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    categoryIds: [] as number[],
    size: '',
    images: [] as string[],
    hoverImage: '',
    stockQuantity: '1',
    productType: '',
    condition: '',
    fitReferenceId: '',
    sold: false
  });

  const [measurements, setMeasurements] = useState<{ [key: string]: string }>({});

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [hoverImageFile, setHoverImageFile] = useState<File | null>(null);
  const [hoverImagePreviewUrl, setHoverImagePreviewUrl] = useState<string>('');

  useEffect(() => {
    loadCategories();
    adminApi.getFitReferences()
      .then(setFitReferences)
      .catch(err => console.error('Failed to load fit references:', err));
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      } else {
        // Fallback to hardcoded categories if API fails
        setCategories([
          { id: 1, name: 'Dresses', description: 'Vintage dresses' },
          { id: 2, name: 'Tops', description: 'Vintage tops and blouses' },
          { id: 3, name: 'Bottoms', description: 'Vintage pants, skirts, shorts' },
          { id: 4, name: 'Outerwear', description: 'Vintage coats and jackets' },
          { id: 5, name: 'Accessories', description: 'Vintage accessories' },
          { id: 6, name: 'Shoes', description: 'Vintage footwear' }
        ]);
      }
    } catch (err: any) {
      console.error('Failed to load categories:', err);
      // Fallback to hardcoded categories
      setCategories([
        { id: 1, name: 'Dresses', description: 'Vintage dresses' },
        { id: 2, name: 'Tops', description: 'Vintage tops and blouses' },
        { id: 3, name: 'Bottoms', description: 'Vintage pants, skirts, shorts' },
        { id: 4, name: 'Outerwear', description: 'Vintage coats and jackets' },
        { id: 5, name: 'Accessories', description: 'Vintage accessories' },
        { id: 6, name: 'Shoes', description: 'Vintage footwear' }
      ]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleMeasurementChange = (measurementName: string, value: string) => {
    setMeasurements(prev => ({
      ...prev,
      [measurementName]: value
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setImageFiles(prev => [...prev, ...newFiles]);
      const newPreviews = newFiles.map(f => URL.createObjectURL(f));
      setImagePreviewUrls(prev => [...prev, ...newPreviews]);
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleHoverImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (hoverImagePreviewUrl) URL.revokeObjectURL(hoverImagePreviewUrl);
      setHoverImageFile(file);
      setHoverImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeHoverImage = () => {
    if (hoverImagePreviewUrl) URL.revokeObjectURL(hoverImagePreviewUrl);
    setHoverImagePreviewUrl('');
    setHoverImageFile(null);
  };

  const selectedCategoryNames = categories
    .filter(cat => formData.categoryIds.includes(cat.id))
    .map(cat => cat.name);

  // "Go Live" publishes the product right away, "Go Draft" keeps it hidden in Draft
  const submitProduct = async (status: 'published' | 'draft') => {
    setLoading(true);
    setSubmittingStatus(status);
    setError('');
    setSuccess('');

    try {
      // No field is mandatory — empty values are sent as blanks/defaults
      // Upload images to R2 on submit
      if (imageFiles.length > 0) setSuccess('Uploading images...');
      const uploadedImageUrls: string[] = [];
      for (const file of imageFiles) {
        const result = await adminApi.uploadImage(file);
        uploadedImageUrls.push(result.imageUrl);
      }

      let uploadedHoverUrl = '';
      if (hoverImageFile) {
        const result = await adminApi.uploadImage(hoverImageFile);
        uploadedHoverUrl = result.imageUrl;
      }

      setSuccess('Creating product...');
      const stockQuantity = formData.stockQuantity === '' ? 1 : Math.max(0, parseInt(formData.stockQuantity) || 0);
      const productData: Partial<AdminProduct> = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: parseFloat(formData.price) || 0,
        originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : undefined,
        categoryIds: formData.categoryIds,
        size: formData.size.trim() || undefined,
        images: uploadedImageUrls,
        hoverImage: uploadedHoverUrl || undefined,
        inStock: stockQuantity > 0 && !formData.sold,
        isSold: formData.sold,
        stockQuantity,
        measurements: measurements,
        productType: formData.productType || undefined,
        status,
        condition: formData.condition.trim() || undefined,
        fitReferenceId: formData.fitReferenceId ? Number(formData.fitReferenceId) : undefined
      };

      const createdProduct = await adminApi.createProduct(productData);
      
      // Save measurements separately if product was created successfully
      if (createdProduct && Object.keys(measurements).length > 0) {
        await adminApi.saveProductMeasurements(createdProduct.product.id, measurements);
      }
      
      setSuccess(status === 'draft' ? 'Product saved as draft!' : 'Product is live!');
      
      // Reset form
      setTimeout(() => {
        navigate('/admin/products');
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to create product');
    } finally {
      setLoading(false);
      setSubmittingStatus(null);
    }
  };

  return (
    <AdminLayout>
      <div className="add-product-page">
        <div className="page-header">
          <div className="header-content">
            <button 
              className="back-btn"
              onClick={() => navigate('/admin/products')}
            >
              ← Back to Products
            </button>
            <h1>Add New Product</h1>
            <p>Create a new vintage fashion product listing</p>
          </div>
        </div>

        <div className="add-product-container">
          <form onSubmit={(e) => e.preventDefault()} className="add-product-form">
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message">
                <span className="success-icon">✅</span>
                {success}
              </div>
            )}

            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Product Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="categoryIds">Categories</label>
                  <CategoryMultiSelect
                    id="categoryIds"
                    categories={categories}
                    selectedIds={formData.categoryIds}
                    onChange={(ids) => setFormData(prev => ({ ...prev, categoryIds: ids }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter product description"
                  rows={4}
                />
              </div>
            </div>

            {/* Product Type Selection for Belts & Scarves */}
            {(() => {
              const productTypes = getProductTypesForCategories(selectedCategoryNames);
              
              if (productTypes.length > 0) {
                return (
                  <div className="form-section">
                    <h3>Product Type</h3>
                    <div className="form-group">
                      <label htmlFor="productType">Product Type</label>
                      <select
                        id="productType"
                        name="productType"
                        value={formData.productType}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Product Type</option>
                        {productTypes.map(type => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Measurements Section */}
            {(() => {
              const categoryName = selectedCategoryNames.join(', ');
              const measurementFields = getMeasurementsForCategories(selectedCategoryNames, formData.productType);
              
              if (measurementFields.length > 0) {
                return (
                  <div className="form-section">
                    <h3>📏 Measurements for {categoryName}{formData.productType && ` - ${formData.productType}`}</h3>
                    <div className="measurements-grid">
                      {measurementFields.map((field) => (
                        <div key={field.name} className="form-group">
                          <label htmlFor={`measurement-${field.name}`}>
                            {field.label}
                          </label>
                          <input
                            type="text"
                            id={`measurement-${field.name}`}
                            name={`measurement-${field.name}`}
                            value={measurements[field.name] || ''}
                            onChange={(e) => handleMeasurementChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Pricing */}
            <div className="form-section">
              <h3>Pricing</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">Current Price (€)</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="originalPrice">Original Price (€)</label>
                  <input
                    type="number"
                    id="originalPrice"
                    name="originalPrice"
                    value={formData.originalPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Size & Stock */}
            <div className="form-section">
              <h3>Size & Stock</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="size">Product Size</label>
                  <input
                    type="text"
                    id="size"
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    placeholder="Enter size (e.g., S, M, L, XL, 38, 40)"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="stockQuantity">Stock Quantity</label>
                  <input
                    type="number"
                    id="stockQuantity"
                    name="stockQuantity"
                    value={formData.stockQuantity}
                    onChange={handleInputChange}
                    placeholder="Available quantity"
                    min="0"
                  />
                </div>
              </div>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="sold"
                  checked={formData.sold}
                  onChange={handleInputChange}
                />
                Sold (stays in the shop with a SOLD badge, can't be added to cart)
              </label>
            </div>

            {/* Condition & Fit Reference */}
            <div className="form-section">
              <h3>Condition & Fit Reference</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="condition">Condition</label>
                  <input
                    type="text"
                    id="condition"
                    name="condition"
                    value={formData.condition}
                    onChange={handleInputChange}
                    placeholder="e.g., Excellent, small mark on the sleeve"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="fitReferenceId">Fit Reference</label>
                  <select
                    id="fitReferenceId"
                    name="fitReferenceId"
                    value={formData.fitReferenceId}
                    onChange={handleInputChange}
                  >
                    <option value="">No fit reference</option>
                    {fitReferences.map(ref => (
                      <option key={ref.id} value={ref.id}>
                        {ref.name} ({ref.type === 'model' ? 'Model' : 'Dressform'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="form-section">
              <h3>Product Images</h3>
              
              <div className="array-input">
                <div className="file-upload-container">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="image-upload" className="file-upload-btn">
                    <span className="upload-icon">📁</span>
                    Choose Images
                  </label>
                  <p className="upload-hint">Select multiple images from your computer (JPG, PNG, etc.)</p>
                </div>
                
                {imagePreviewUrls.length > 0 && (
                  <div className="image-previews">
                    {imagePreviewUrls.map((image, index) => (
                      <div key={index} className="image-preview">
                        <img src={image} alt={`Product ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="remove-image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Hover Image */}
            <div className="form-section">
              <h3>Hover Image (Optional)</h3>
              <p className="section-description">Upload an image that will be displayed when users hover over the product card</p>
              
              <div className="array-input">
                <div className="file-upload-container">
                  <input
                    type="file"
                    id="hover-image-upload"
                    accept="image/*"
                    onChange={handleHoverImageUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="hover-image-upload" className="file-upload-btn">
                    <span className="upload-icon">🖼️</span>
                    Choose Hover Image
                  </label>
                  <p className="upload-hint">Select a single image for hover effect (JPG, PNG, etc.)</p>
                </div>
                
                {hoverImagePreviewUrl && (
                  <div className="image-previews">
                    <div className="image-preview">
                      <img src={hoverImagePreviewUrl} alt="Hover preview" />
                      <button
                        type="button"
                        onClick={removeHoverImage}
                        className="remove-image"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/admin/products')}
                className="cancel-btn"
                disabled={loading}
              >
                Cancel
              </button>
              
              <button
                type="button"
                className="submit-btn draft-btn"
                disabled={loading}
                onClick={() => submitProduct('draft')}
              >
                {submittingStatus === 'draft' ? (
                  <>
                    <span className="loading-spinner"></span>
                    Saving Draft...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">🌙</span>
                    Go Draft
                  </>
                )}
              </button>

              <button
                type="button"
                className="submit-btn"
                disabled={loading}
                onClick={() => submitProduct('published')}
              >
                {submittingStatus === 'published' ? (
                  <>
                    <span className="loading-spinner"></span>
                    Going Live...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">✨</span>
                    Go Live
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddProduct;